/**
 * Auth state and token management.
 *
 * Access tokens come from the OIDC authorization-code-with-PKCE flow against
 * whichever provider `VITE_OIDC_ISSUER` points at (see `oidc.ts`). The access
 * token lives in localStorage; the refresh token also lives in localStorage
 * for MVP simplicity. A future iteration should move the refresh token to an
 * HttpOnly cookie via a backend proxy endpoint, but that buys little until we
 * ship to a real domain with HTTPS.
 */

import * as oidc from "./oidc";

const ACCESS_TOKEN_KEY = "access_token";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}

/**
 * Refresh the access token using the stored OIDC refresh token.
 * Returns the new access token on success, or null if refresh fails (in which
 * case the caller should redirect to sign-in).
 *
 * The `apiBase` parameter is unused — kept for backwards compatibility with
 * callers in `api.ts` that pass it.
 */
export async function refreshAccessToken(
  _apiBase?: string,
): Promise<string | null> {
  return oidc.refresh();
}

/**
 * Sign out — invalidates the provider session (if it supports RP-initiated
 * logout) and clears local tokens.
 */
export async function signOut(_apiBase?: string): Promise<void> {
  await oidc.signOut();
  clearAccessToken();
}
